import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, FileUp, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Buffer } from 'buffer'
import { trpc } from '@/lib/trpc'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@deepguide-ai/ui/dialog'

interface SystemDescriptionTemplateBannerProps {
  programId: string
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

export function SystemDescriptionTemplateBanner({
  programId,
}: SystemDescriptionTemplateBannerProps) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const templateInfoQuery = useQuery(
    trpc.complianceProgram.getSystemDescriptionTemplateInfo.queryOptions({
      programId,
    })
  )

  const templateInfoKey =
    trpc.complianceProgram.getSystemDescriptionTemplateInfo.queryKey({
      programId,
    })

  const uploadMutation = useMutation(
    (trpc.complianceProgram.uploadSystemDescriptionTemplate.mutationOptions() as any)
  )

  const removeMutation = useMutation(
    (trpc.complianceProgram.removeSystemDescriptionTemplate.mutationOptions() as any)
  )

  const templateInfo = templateInfoQuery.data
  const hasCustomTemplate = templateInfo?.hasCustomTemplate ?? false
  const templateFilename = templateInfo?.templateFilename ?? null

  function validateFile(file: File): string | null {
    if (!file.name.toLowerCase().endsWith('.docx')) {
      return 'Only .docx files are accepted'
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'File must be 10MB or less'
    }
    return null
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const error = validateFile(file)
    if (error) {
      toast.error(error)
      return
    }
    setSelectedFile(file)
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(true)
  }

  function handleDragLeave() {
    setIsDragging(false)
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const error = validateFile(file)
    if (error) {
      toast.error(error)
      return
    }
    setSelectedFile(file)
  }

  async function handleUpload() {
    if (!selectedFile) return

    try {
      const content = await selectedFile.arrayBuffer()
      const base64Data = Buffer.from(content).toString('base64')

      await uploadMutation.mutateAsync({
        programId,
        filename: selectedFile.name,
        contentType: selectedFile.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        base64Data,
        size: selectedFile.size,
      })

      await queryClient.invalidateQueries({ queryKey: templateInfoKey })
      toast.success('Template uploaded successfully')
      setSelectedFile(null)
      setDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload template'
      toast.error(message)
    }
  }

  async function handleRemove() {
    try {
      await removeMutation.mutateAsync({ programId })
      await queryClient.invalidateQueries({ queryKey: templateInfoKey })
      toast.success('Custom template removed. Reverted to default Screenata template.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove template'
      toast.error(message)
    }
  }

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open)
    if (!open) {
      setSelectedFile(null)
    }
  }

  if (templateInfoQuery.isLoading) {
    return null
  }

  return (
    <div className='flex items-center gap-3 rounded-sm border border-border bg-card px-4 py-2.5'>
      <div className='flex flex-1 items-center gap-2'>
        <span className='text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
          Export Template
        </span>
        <span className='text-border'>·</span>
        {hasCustomTemplate && templateFilename ? (
          <span className='text-[12px] font-medium text-text-secondary'>
            {templateFilename}
          </span>
        ) : (
          <span className='text-[12px] text-text-body'>
            Using default Screenata template
          </span>
        )}
      </div>

      <div className='flex items-center gap-2'>
        {hasCustomTemplate && (
          <button
            onClick={() => {
              void handleRemove()
            }}
            disabled={removeMutation.isPending}
            className='inline-flex items-center gap-1 rounded-sm border border-border-divider bg-card px-2.5 py-1 text-[11px] font-medium text-text-subtle transition-colors hover:border-[#C0C0C0] hover:text-text-body disabled:opacity-50'
          >
            {removeMutation.isPending ? (
              <Loader2 className='h-3 w-3 animate-spin' />
            ) : (
              <X className='h-3 w-3' />
            )}
            Remove
          </button>
        )}

        <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogTrigger asChild>
            <button className='inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90'>
              <Upload className='h-3 w-3' />
              {hasCustomTemplate ? 'Replace Template' : 'Upload Template'}
            </button>
          </DialogTrigger>
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle className='text-[15px] font-semibold text-text-secondary'>
                Upload Auditor Template
              </DialogTitle>
            </DialogHeader>

            <div className='mt-1 space-y-4'>
              <p className='text-[12px] text-text-subtle'>
                Upload your auditor&apos;s .docx template to use instead of the
                default Screenata template when exporting the system description.
              </p>

              <div
                className={`flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed px-4 py-8 transition-colors ${
                  isDragging || selectedFile
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-surface-subtle hover:border-primary hover:bg-primary/5'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
                role='button'
                tabIndex={0}
              >
                {selectedFile ? (
                  <div className='flex flex-col items-center gap-2'>
                    <FileUp className='h-7 w-7 text-cobalt-strong' />
                    <span className='text-center text-[12px] font-medium text-text-secondary'>
                      {selectedFile.name}
                    </span>
                    <span className='text-[11px] text-text-subtle'>
                      {(selectedFile.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type='button'
                      className='mt-1 text-[11px] text-text-subtle underline hover:text-text-body'
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                        if (fileInputRef.current) {
                          fileInputRef.current.value = ''
                        }
                      }}
                    >
                      Choose a different file
                    </button>
                  </div>
                ) : (
                  <div className='flex flex-col items-center gap-2'>
                    <Upload className='h-7 w-7 text-text-placeholder' />
                    <span className='text-[12px] text-text-body'>
                      Drag &amp; drop or{' '}
                      <span className='font-medium text-cobalt-strong'>
                        browse
                      </span>
                    </span>
                    <span className='text-[11px] text-text-placeholder'>
                      .docx only · max 10MB
                    </span>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type='file'
                accept='.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                className='hidden'
                onChange={handleFileChange}
              />

              <div className='flex items-center justify-end gap-2'>
                <button
                  type='button'
                  onClick={() => setDialogOpen(false)}
                  className='inline-flex items-center rounded-sm border border-border-divider bg-card px-3 py-1.5 text-[12px] font-medium text-text-body transition-colors hover:bg-muted'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={() => {
                    void handleUpload()
                  }}
                  disabled={!selectedFile || uploadMutation.isPending}
                  className='inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className='h-3 w-3 animate-spin' />
                  ) : (
                    <FileUp className='h-3 w-3' />
                  )}
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
